import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import API_CONFIG, { getFullUrl } from "../config/apiConfig";

// ─── Keys ─────────────────────────────────────────────────────────────────────
const VISITOR_ID_KEY  = "gv_visitor_id";
const VISITOR_CNT_KEY = "gv_visitor_count"; // sessionStorage → survives tab refresh

// ─── Persistent visitor ID ────────────────────────────────────────────────────
// Also written as a cookie so the backend can deduplicate by visitor_id.
const getOrCreateVisitorId = () => {
  let id = localStorage.getItem(VISITOR_ID_KEY);
  if (!id) {
    id = crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(VISITOR_ID_KEY, id);
  }
  // Sync to cookie (1-year, SameSite=Lax so backend receives it on GET)
  document.cookie = `visitor_id=${id}; max-age=${60 * 60 * 24 * 365}; path=/; SameSite=Lax`;
  return id;
};

// ─── Module-level cache ────────────────────────────────────────────────────────
// Seeded from sessionStorage so page refresh won't trigger a new API call.
let cachedCount = (() => {
  const stored = sessionStorage.getItem(VISITOR_CNT_KEY);
  return stored !== null ? Number(stored) : null;
})();

function VisitorCounter() {
  const { i18n } = useTranslation();
  const [count, setCount] = useState(cachedCount ?? 0);

  useEffect(() => {
    // ── Already cached in this tab session → show it, skip API call.
    //    Prevents count from incrementing on every page refresh.
    if (cachedCount !== null) {
      setCount(cachedCount);
      return;
    }

    // `active` tracks whether THIS effect instance is still alive.
    // React StrictMode: mount → cleanup (active=false, abort) → remount (active=true, fresh fetch).
    // The first fetch gets aborted (caught & ignored). The second one succeeds. ✅
    let active = true;
    const controller = new AbortController();
    const url = getFullUrl(API_CONFIG.ENDPOINTS.VISITORS || "/v1/global/visitors");
    const visitorId = getOrCreateVisitorId();

    const headers = {
      "Content-Type": "application/json",
      "X-Visitor-ID": visitorId, // header-based ID for backends that prefer headers
    };
    const token = localStorage.getItem("token");
    if (token) headers["Authorization"] = `Bearer ${token}`;

    fetch(url, {
      method: "GET",
      headers,
      credentials: "include",
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!active) return; // stale effect — ignore result
        let countValue = null;
        if (typeof data === "number") {
          countValue = data;
        } else if (data && typeof data === "object") {
          countValue =
            data.totalVisitors ??
            data.count ??
            data.visitorCount ??
            data.total ??
            (typeof data.data === "number"
              ? data.data
              : data.data?.totalVisitors ?? data.data?.count) ??
            null;
        }

        if (countValue !== null && !isNaN(Number(countValue))) {
          const num = Number(countValue);
          // Persist in sessionStorage — refresh won't re-call the API
          sessionStorage.setItem(VISITOR_CNT_KEY, String(num));
          cachedCount = num;
          setCount(num);
        } else {
          console.warn("VisitorCounter: unexpected response shape", data);
        }
      })
      .catch((err) => {
        if (err.name === "AbortError") return; // expected on StrictMode cleanup
        if (!active) return;
        console.error("VisitorCounter fetch error:", err);
      });

    return () => {
      active = false;     // mark this effect as stale
      controller.abort(); // cancel in-flight request
    };
  }, []);

  // Convert English digits → Marathi digits
  const toMarathiDigits = (num) =>
    num.toString().replace(/\d/g, (d) => "०१२३४५६७८९"[d]);

  const displayCount =
    i18n.language === "mr" ? toMarathiDigits(count) : count.toString();
  const digits = displayCount.padStart(4, "0").split("");

  return (
    <div style={{ textAlign: "center", marginTop: "4px" }}>
      <p style={{ color: "white", fontSize: "14px", marginBottom: "2px" }}>
        {i18n.language === "mr" ? "भेटींची संख्या" : "Visitor Count"}
      </p>
      <div style={{ display: "flex", justifyContent: "center", gap: "3px" }}>
        {digits.map((digit, idx) => (
          <div
            key={idx}
            style={{
              width: "22px",
              height: "28px",
              backgroundColor: "#fff",
              color: "#000",
              fontWeight: "bold",
              fontFamily: "monospace",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
            }}
          >
            {digit}
          </div>
        ))}
      </div>
    </div>
  );
}

export default VisitorCounter;

